import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, User, Phone, Mail, Lock, FileText, MapPin, Loader2 } from "lucide-react";
import { api, formatBRL, formatApiErrorDetail } from "../../lib/api";
import { loadQuote, savePending } from "../../lib/booking-storage";
import { useSEO } from "../../lib/seo";
import { track } from "../../lib/tracking";
import { getUTM } from "../../lib/utm";
import { maskPhoneBR, sanitizePhone } from "../../lib/phone";
import {
  maskCPF, isValidCPF, sanitizeDigits, maskCEP, isValidCEP,
  fetchAddressByCEP, maskRG, BR_STATES,
} from "../../lib/documents";
import { toast } from "sonner";
import PackageBanner from "../../components/booking/PackageBanner";

const INPUT_CLS =
  "w-full rounded-xl border-2 border-gray-200 px-4 py-3 text-base bg-white text-[#0A1128] placeholder:text-gray-400 focus:border-[#D4AF37] focus:outline-none";
const LBL_CLS =
  "text-xs uppercase tracking-widest text-gray-600 font-bold flex items-center gap-1.5 mb-2";

export default function ReservarIdentificacao() {
  const nav = useNavigate();
  const saved = loadQuote();
  const [form, setForm] = useState({
    guest_name: "", guest_phone: "", guest_email: "",
    guest_cpf: "", guest_rg: "",
    address_cep: "", address_street: "", address_number: "", address_complement: "",
    address_neighborhood: "", address_city: "", address_state: "",
    payment_method: "pix",
  });
  const [loading, setLoading] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);
  const [hourBankBlock, setHourBankBlock] = useState(false);

  useSEO({ title: "Identificação", description: "Quase lá!" });

  useEffect(() => {
    if (!saved) nav("/espacos", { replace: true });
  }, [saved, nav]);

  if (!saved) return null;
  const { space, quote } = saved;
  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const onCepBlur = async () => {
    if (!isValidCEP(form.address_cep)) return;
    setCepLoading(true);
    const addr = await fetchAddressByCEP(form.address_cep);
    setCepLoading(false);
    if (addr) {
      update({
        address_street: addr.street || form.address_street,
        address_neighborhood: addr.neighborhood || form.address_neighborhood,
        address_city: addr.city || form.address_city,
        address_state: addr.state || form.address_state,
      });
    } else {
      toast.warning("CEP não encontrado — preencha manualmente.");
    }
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (loading) return;
    if (!isValidCPF(form.guest_cpf)) return toast.error("CPF inválido — verifique os dígitos.");
    if (!isValidCEP(form.address_cep)) return toast.error("CEP inválido — 8 dígitos.");
    if (!form.address_state || form.address_state.length !== 2) return toast.error("Selecione o estado.");

    setLoading(true);
    try {
      const utm = getUTM() || {};
      const inferredSource = utm.utm_source
        ? `${utm.utm_source}${utm.utm_medium ? "/" + utm.utm_medium : ""}`
        : "site";
      const { data } = await api.post("/public/reservations", {
        space_id: space.id,
        start_time: quote.start_time,
        end_time: quote.end_time,
        guest_name: form.guest_name.trim(),
        guest_phone: sanitizePhone(form.guest_phone),
        guest_email: form.guest_email.trim().toLowerCase(),
        guest_cpf: sanitizeDigits(form.guest_cpf),
        guest_rg: form.guest_rg.trim(),
        address_cep: sanitizeDigits(form.address_cep),
        address_street: form.address_street.trim(),
        address_number: form.address_number.trim(),
        address_complement: form.address_complement.trim() || null,
        address_neighborhood: form.address_neighborhood.trim(),
        address_city: form.address_city.trim(),
        address_state: form.address_state.trim().toUpperCase(),
        payment_method: form.payment_method,
        source: inferredSource,
      });
      savePending({
        reservation_id: data.reservation_id,
        public_token: data.public_token,
        expires_at: data.expires_at,
        space_name: space.name,
        pix_copy_paste: data.pix_copy_paste,
        pix_qrcode: data.pix_qrcode,
        payment_link: data.payment_link,
        boleto_url: data.boleto_url,
        payment_method: form.payment_method,
      });
      track("payment_started", { reservation_id: data.reservation_id, total: data.amount });
      nav(`/reservar/pagamento?rid=${data.reservation_id}&token=${data.public_token}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === "has_active_hour_bank") {
        setHourBankBlock(true);
        return;
      }
      const msg = formatApiErrorDetail(detail) || "Não foi possível criar a reserva";
      toast.error(msg);
      if (e?.response?.status === 409) {
        setTimeout(() => nav(`/espacos/${space.id}`), 1500);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    {hourBankBlock && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" data-testid="hourbank-block-modal">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center">
          <h2 className="text-lg font-bold text-[#0A1128]">Você possui um Banco de Horas ativo.</h2>
          <p className="text-sm text-gray-600 mt-2">Suas reservas devem ser realizadas pelo Portal do Cliente.</p>
          <Link to="/portal/reservas" data-testid="hourbank-go-portal"
            className="mt-6 inline-block w-full bg-[#0A1128] text-white rounded-xl py-3.5 font-bold uppercase tracking-wider text-sm hover:bg-[#14213D]">
            Acessar meu painel
          </Link>
        </div>
      </div>
    )}
    <div className="bg-gray-50 min-h-screen pb-12" data-testid="identificacao-page">
      <div className="sticky top-0 z-30 bg-white/80 backdrop-blur border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/reservar/cotacao" className="flex items-center gap-2 text-sm font-semibold text-[#0A1128]" data-testid="back-to-cotacao">
            <ArrowLeft size={18} /> Voltar
          </Link>
          <div className="text-xs uppercase tracking-widest text-gray-500">
            Etapa <span className="text-[#D4AF37] font-bold">2</span> de 3
          </div>
        </div>
        <div className="h-1 bg-gray-200">
          <div className="h-full bg-[#D4AF37]" style={{ width: "66%" }} />
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8 grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8">
        <form onSubmit={submit} className="space-y-6">
          <header>
            <h1 className="font-display text-3xl text-[#0A1128] leading-tight">Quase lá!</h1>
            <p className="text-gray-600 mt-1">Precisamos dos seus dados completos para confirmar a reserva.</p>
          </header>

          {/* Dados pessoais */}
          <section className="space-y-4">
            <h2 className="text-sm font-bold uppercase tracking-widest text-gray-700">Dados pessoais</h2>
            <div>
              <label className={LBL_CLS}><User size={14} /> Nome completo</label>
              <input required type="text" autoComplete="name" minLength={2}
                value={form.guest_name}
                onChange={(e) => update({ guest_name: e.target.value })}
                data-testid="input-guest-name" placeholder="Ex: Maria Silva"
                className={INPUT_CLS} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={LBL_CLS}><Phone size={14} /> WhatsApp</label>
                <input required type="tel" inputMode="tel" autoComplete="tel"
                  value={form.guest_phone}
                  onChange={(e) => update({ guest_phone: maskPhoneBR(e.target.value) })}
                  data-testid="input-guest-phone" placeholder="(21) 99999-0000"
                  className={INPUT_CLS} />
              </div>
              <div>
                <label className={LBL_CLS}><Mail size={14} /> E-mail</label>
                <input required type="email" autoComplete="email" inputMode="email"
                  value={form.guest_email}
                  onChange={(e) => update({ guest_email: e.target.value })}
                  data-testid="input-guest-email" placeholder="seu@email.com"
                  className={INPUT_CLS} />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={LBL_CLS}><FileText size={14} /> CPF</label>
                <input required inputMode="numeric" value={form.guest_cpf}
                  onChange={(e) => update({ guest_cpf: maskCPF(e.target.value) })}
                  data-testid="input-guest-cpf" placeholder="000.000.000-00"
                  className={INPUT_CLS} />
              </div>
              <div>
                <label className={LBL_CLS}><FileText size={14} /> RG</label>
                <input required value={form.guest_rg}
                  onChange={(e) => update({ guest_rg: maskRG(e.target.value) })}
                  data-testid="input-guest-rg" placeholder="00.000.000-0"
                  className={INPUT_CLS} />
              </div>
            </div>
          </section>

          {/* RC3 Lite — Banner inteligente de pacote ativo */}
          <PackageBanner
            email={form.guest_email}
            cpf={form.guest_cpf}
            hoursNeeded={quote?.hours}
          />

          {/* Endereço */}
          <section className="space-y-4">
            <h2 className="text-sm font-bold uppercase tracking-widest text-gray-700 flex items-center gap-1.5">
              <MapPin size={14} /> Endereço
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-3">
              <div>
                <label className={LBL_CLS}>CEP</label>
                <div className="relative">
                  <input required inputMode="numeric" value={form.address_cep}
                    onChange={(e) => update({ address_cep: maskCEP(e.target.value) })}
                    onBlur={onCepBlur}
                    data-testid="input-address-cep" placeholder="00000-000"
                    className={INPUT_CLS} />
                  {cepLoading && (
                    <Loader2 size={16} className="animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  )}
                </div>
              </div>
              <div>
                <label className={LBL_CLS}>Rua</label>
                <input required value={form.address_street}
                  onChange={(e) => update({ address_street: e.target.value })}
                  data-testid="input-address-street" className={INPUT_CLS} />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-3">
              <div>
                <label className={LBL_CLS}>Número</label>
                <input required value={form.address_number}
                  onChange={(e) => update({ address_number: e.target.value })}
                  inputMode="numeric" data-testid="input-address-number" className={INPUT_CLS} />
              </div>
              <div>
                <label className={LBL_CLS}>Complemento</label>
                <input value={form.address_complement}
                  onChange={(e) => update({ address_complement: e.target.value })}
                  placeholder="Apto, bloco, ponto de referência"
                  data-testid="input-address-complement" className={INPUT_CLS} />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className={LBL_CLS}>Bairro</label>
                <input required value={form.address_neighborhood}
                  onChange={(e) => update({ address_neighborhood: e.target.value })}
                  data-testid="input-address-neighborhood" className={INPUT_CLS} />
              </div>
              <div>
                <label className={LBL_CLS}>Cidade</label>
                <input required value={form.address_city}
                  onChange={(e) => update({ address_city: e.target.value })}
                  data-testid="input-address-city" className={INPUT_CLS} />
              </div>
              <div>
                <label className={LBL_CLS}>Estado</label>
                <select required value={form.address_state}
                  onChange={(e) => update({ address_state: e.target.value })}
                  data-testid="input-address-state" className={INPUT_CLS}>
                  <option value="">UF</option>
                  {BR_STATES.map((uf) => <option key={uf} value={uf}>{uf}</option>)}
                </select>
              </div>
            </div>
          </section>

          {/* Payment method — PIX direto ou Cartão (Checkout Pro) */}
          <div>
            <label className="text-xs uppercase tracking-widest text-gray-600 font-bold mb-2 block">
              Forma de pagamento
            </label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { v: "pix", label: "PIX", hint: "QR Code + Copia e Cola" },
                { v: "cartao", label: "Cartão", hint: "Crédito" },
              ].map((opt) => (
                <button type="button" key={opt.v}
                  onClick={() => update({ payment_method: opt.v })}
                  data-testid={`payment-method-${opt.v}`}
                  className={[
                    "rounded-xl border-2 py-3 text-center transition-all active:scale-95",
                    form.payment_method === opt.v
                      ? "bg-[#0A1128] border-[#0A1128] text-white"
                      : "bg-white border-gray-200 text-[#0A1128] hover:border-[#0A1128]/40",
                  ].join(" ")}>
                  <div className="font-bold">{opt.label}</div>
                  <div className="text-[10px] uppercase opacity-70">{opt.hint}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-xl bg-blue-50 border border-blue-200 p-3 text-xs flex items-start gap-2.5">
            <Lock size={16} className="text-blue-600 shrink-0 mt-0.5" />
            <div className="text-blue-900 leading-relaxed">
              <strong>Criaremos uma conta automaticamente</strong> para você acompanhar sua reserva.
              Sem senha agora — enviaremos um link de acesso por e-mail.
            </div>
          </div>

          <button type="submit" disabled={loading}
            data-testid="submit-identificacao"
            className={[
              "w-full rounded-xl py-4 font-bold uppercase tracking-wider text-sm transition-all",
              loading ? "bg-gray-400 text-white cursor-wait" : "bg-[#0A1128] text-white hover:bg-[#14213D] active:scale-[0.99]",
            ].join(" ")}>
            {loading ? "Criando reserva..." : "Continuar para pagamento →"}
          </button>

          <div className="text-center text-xs text-gray-500">
            Já tem conta?{" "}
            <Link to="/login" className="text-[#D4AF37] font-semibold underline">Faça login</Link>
          </div>
        </form>

        {/* Sticky summary */}
        <aside className="lg:sticky lg:top-20 lg:self-start">
          <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden" data-testid="summary-aside">
            {space.photo && <img src={space.photo} alt={space.name} className="w-full aspect-[16/9] object-cover" />}
            <div className="p-4 space-y-2 text-sm">
              <div className="text-xs uppercase tracking-widest text-[#D4AF37] font-bold">{space.category}</div>
              <div className="font-bold text-[#0A1128]">{space.name}</div>
              <div className="text-gray-600">
                {new Date(quote.start_time).toLocaleDateString("pt-BR", { day: "2-digit", month: "long" })}
                {" · "}
                {new Date(quote.start_time).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                {" às "}
                {new Date(quote.end_time).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
              </div>
              <div className="border-t border-gray-200 pt-3 mt-3 flex items-baseline justify-between">
                <span className="font-semibold text-gray-600">Total</span>
                <span className="text-2xl font-black text-[#0A1128]">{formatBRL(quote.total_avulso)}</span>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
    </>
  );
}
